import { useState, useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../msalConfig';
import { userHasNotesAccess } from '../config/notesAccess';
import { searchEverything } from '../oneNoteApi';

const CHIPS = [
  { key: 'all',      label: 'All' },
  { key: 'note',     label: 'Notes' },
  { key: 'photo',    label: 'Photos' },
  { key: 'document', label: 'Documents' },
  { key: 'fig',      label: 'FIG' },
  { key: 'other',    label: 'Other' },
];

const TYPE_COLORS = {
  note:     '#4B0082',
  photo:    '#2e7d32',
  document: '#1565c0',
  fig:      '#B8860B',
  other:    '#616161',
};

const TYPE_LABELS = {
  note:     'Note',
  photo:    'Photo',
  document: 'Doc',
  fig:      'FIG',
  other:    'Other',
};

const SECTION_HEADERS = {
  note:     'Notes',
  photo:    'Photos',
  document: 'Documents',
  fig:      'FIG',
  other:    'Other',
};

const GROUP_ORDER = ['note', 'photo', 'document', 'fig', 'other'];

function cleanPath(path) {
  if (!path) return '';
  const m = path.match(/root:(.*)/);
  return m ? m[1].replace(/^\//, '') : '';
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function openResult(result) {
  if (result.resultType === 'photo') {
    window.location.href = `/familyvault/photos/${result.id}`;
    return;
  }
  if (!result.webUrl) return;
  if (result.resultType === 'note') {
    const pathPart = result.webUrl.split('?')[0];
    const href = /\.one(\/|$)/i.test(pathPart) ? 'onenote:' + result.webUrl : result.webUrl;
    if (href.startsWith('onenote:')) {
      window.location.href = href;
    } else {
      window.open(result.webUrl, '_blank', 'noopener');
    }
  } else {
    window.open(result.webUrl, '_blank', 'noopener');
  }
}

function ResultRow({ r }) {
  const path = cleanPath(r.parentPath);
  const meta = [path, fmtDate(r.lastModified)].filter(Boolean).join('  ·  ');
  return (
    <div style={S.row} onClick={() => openResult(r)}>
      <span style={S.badge(r.resultType)}>{TYPE_LABELS[r.resultType]}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={S.resultName}>{r.name}</span>
        {meta && <span style={S.resultMeta}>{meta}</span>}
      </div>
    </div>
  );
}

const S = {
  root: {
    maxWidth: 680, margin: '0 auto',
    padding: '24px 16px 80px',
    fontFamily: 'inherit',
  },
  heading: {
    fontSize: 22, fontWeight: 700, color: '#222',
    margin: '0 0 16px',
  },
  input: {
    display: 'block', width: '100%', boxSizing: 'border-box',
    fontSize: 15, padding: '10px 14px',
    border: '1px solid #ddd', borderRadius: 8,
    outline: 'none', background: '#fff',
    marginBottom: 14,
  },
  chipsRow: {
    display: 'flex', gap: 8, flexWrap: 'wrap',
    marginBottom: 20,
  },
  chip: (active) => ({
    padding: '5px 14px', borderRadius: 20,
    fontSize: 12, fontWeight: 600,
    cursor: 'pointer',
    border: `1.5px solid ${active ? '#B8860B' : '#ddd'}`,
    background: active ? '#B8860B' : '#fff',
    color: active ? '#fff' : '#555',
    transition: 'all 0.15s',
    userSelect: 'none',
    lineHeight: 1.4,
  }),
  sectionLabel: {
    fontSize: 11, fontWeight: 700, color: '#aaa',
    textTransform: 'uppercase', letterSpacing: 0.6,
    margin: '18px 0 6px',
  },
  row: {
    display: 'flex', alignItems: 'flex-start',
    padding: '10px 4px', borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer', gap: 10,
  },
  badge: (type) => ({
    flexShrink: 0, padding: '2px 7px', borderRadius: 4,
    fontSize: 10, fontWeight: 700, letterSpacing: '0.3px',
    background: TYPE_COLORS[type] || TYPE_COLORS.other,
    color: '#fff', marginTop: 2,
  }),
  resultName: {
    fontSize: 13, fontWeight: 500, color: '#1a73e8',
    display: 'block',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  resultMeta: {
    fontSize: 11, color: '#aaa', display: 'block', marginTop: 2,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  empty:  { color: '#bbb', fontSize: 13, margin: '16px 0' },
  status: { color: '#aaa', fontSize: 13, margin: '8px 0' },
};

export default function SearchDestination() {
  const { instance, accounts } = useMsal();
  const isAllowed = userHasNotesAccess(accounts[0]);

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const res = await instance.acquireTokenSilent({ ...graphRequest, account });
    return res.accessToken;
  }, [instance, accounts]);

  const [query,      setQuery]      = useState('');
  const [results,    setResults]    = useState([]);
  const [searching,  setSearching]  = useState(false);
  const [error,      setError]      = useState(null);
  const [activeChip, setActiveChip] = useState('all');

  const runSearch = useCallback(async (q) => {
    setError(null);
    setResults([]);
    try {
      const token = await getToken();
      const hits  = await searchEverything(token, q);
      setResults(hits);
    } catch (err) {
      setError(err.message);
    } finally {
      setSearching(false);
    }
  }, [getToken]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setError(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => runSearch(q), 600);
    return () => clearTimeout(timer);
  }, [query, runSearch]);

  if (!isAllowed) {
    return <div style={{ padding: 24, color: '#999', fontSize: 14 }}>Not authorized.</div>;
  }

  const isActive = query.trim().length >= 2;

  const counts = { all: results.length };
  for (const r of results) counts[r.resultType] = (counts[r.resultType] || 0) + 1;

  const displayed = results.filter(r => activeChip === 'all' || r.resultType === activeChip);

  const grouped = GROUP_ORDER
    .map(type => ({ type, items: results.filter(r => r.resultType === type) }))
    .filter(g => g.items.length > 0);

  return (
    <div style={S.root}>
      <h2 style={S.heading}>Search</h2>

      <input
        type="text"
        placeholder="Search across notes, documents, and FIG files…"
        value={query}
        onChange={e => { setQuery(e.target.value); setActiveChip('all'); }}
        style={S.input}
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        autoFocus
      />

      {isActive && (
        <div style={S.chipsRow}>
          {CHIPS.map(chip => (
            <button
              key={chip.key}
              style={S.chip(activeChip === chip.key)}
              onClick={() => setActiveChip(chip.key)}
            >
              {chip.label}{!searching && counts[chip.key] != null ? ` (${counts[chip.key]})` : ''}
            </button>
          ))}
        </div>
      )}

      {error && (
        <p style={{ color: '#c0392b', fontSize: 13, margin: '0 0 12px' }}>{error}</p>
      )}

      {isActive && searching && (
        <p style={S.status}>Searching…</p>
      )}

      {isActive && !searching && results.length === 0 && !error && (
        <p style={S.empty}>No results. Try different keywords.</p>
      )}

      {/* Single chip — flat list */}
      {isActive && !searching && activeChip !== 'all' && (
        displayed.length > 0
          ? displayed.map((r, i) => <ResultRow key={`${r.id}-${i}`} r={r} />)
          : <p style={S.empty}>No {SECTION_HEADERS[activeChip] || activeChip} results.</p>
      )}

      {/* All chip — grouped by type */}
      {isActive && !searching && activeChip === 'all' && results.length > 0 && (
        grouped.map(g => (
          <div key={g.type}>
            <p style={S.sectionLabel}>{SECTION_HEADERS[g.type]} ({g.items.length})</p>
            {g.items.map((r, i) => <ResultRow key={`${r.id}-${i}`} r={r} />)}
          </div>
        ))
      )}
    </div>
  );
}
